import type { Edge, Node } from '@xyflow/react'
import type { AgentSettingsFormValues } from '../../config/base.config'
import type {
  EdgeData,
  EvalStreamEntry,
  FlowNodeTypes,
  NodeDataBase,
  SystemPrompt,
} from '../../types'

export enum EvalStatus {
  Pending = 'pending',
  Testing = 'testing',
  Passed = 'passed',
  Failed = 'failed',
  Partial = 'partial',
  Skipped = 'skipped',
}

export enum EvalCancellationSource {
  VibeAgent = 'vibe_agent',
  User = 'user',
  System = 'system',
}

export interface EvalMetric {
  name: string
  score: number
  threshold: number
  reason: string
}

/**
 * Frontend application type for test cases (camelCase)
 * Used throughout the UI components and state management
 * Includes UI-specific fields like status, runId, metrics, etc.
 * Converted from TestCaseData (backend payload) in store.ts when received
 */
export interface EvalTestCase {
  id: string
  name: string
  nodeUuid: string
  userMessage: string
  expectedVariables: Record<string, unknown>
  nodeVariables?: Record<string, unknown>
  conversationHistory: Array<{
    message: string
    role: string
    intent_chosen: string | null
    variables_extracted: Record<string, unknown>
    previous_node?: string | null
    new_node?: string | null
    tool_name?: string | null
    from_node_uuid?: string | null
    to_node_uuid?: string | null
    currentNodeUuid?: string | null
  }>
  rigidityCategory: string

  // Status tracking
  status: EvalStatus.Pending | EvalStatus.Testing | EvalStatus.Passed | EvalStatus.Failed

  // Results (populated when eval:evaluation_result arrives)
  actualResponse?: string
  expectedResponse?: string
  metrics?: EvalMetric[]
  success?: boolean

  // Run tracking
  runId?: number
}

export interface EvalNode {
  nodeId: string
  nodeUuid: string
  nodeName: string
  nodeType: FlowNodeTypes
  testCases: EvalTestCase[]
  isExpanded: boolean
  status?: EvalStatus
  runId: number
  skipReason?: string
}

export interface EvalResult {
  nodes: EvalNode[]
  summary: {
    totalTests: number
    passed: number
    failed: number
    testing: number
  }
}

export interface EvalState {
  currentEvalId: string | null
  currentRunId: number | null
  currentlyTestingNodeUuid: string | null
  events: EvalStreamEntry[]
  evalResults: EvalResult
  isEvalRunning: boolean
  isPaused: boolean
  sseController: AbortController | null
}

export interface EvalActions {
  setCurrentEvalId: (id: string | null) => void
  setCurrentRunId: (id: number | null) => void
  addEvent: (event: EvalStreamEntry) => void
  setSSEController: (controller: AbortController | null) => void
  cancelEval: () => void
  clearEvalStream: () => void
  setIsEvalRunning: (isRunning: boolean) => void
  toggleNodeExpanded: (nodeUuid: string, runId?: number | null) => void
  reset: () => void
}

/**
 * Type for nodes cleaned by cleanNodesForExport
 * Removes UI-specific properties that shouldn't be sent to the API
 */
type CleanedNode = Omit<
  Node<NodeDataBase<unknown>>,
  'position' | 'zIndex' | 'measured' | 'selected'
>

/**
 * Request type for starting or resuming an eval stream
 * Sent to the API with snake_case formatting
 * Frontend converts camelCase to snake_case in the service layer
 */
export interface EvalStreamRequest {
  flow_json: {
    nodes: CleanedNode[]
    edges: Edge<EdgeData>[]
    system_prompt: SystemPrompt | null
    agent_settings: AgentSettingsFormValues | null
    flow_name?: string | null
  }
  eval_id?: string
}

/**
 * Base type for eval stream progress updates
 * Represents the actual event payload shape received from the backend
 * Uses snake_case for backend fields, but frontend code should normalize to camelCase
 */
export interface EvalProgressUpdate {
  eval_id?: string
  run_id?: number
  [key: string]: unknown
}

/**
 * Specific event payload types for common eval events
 * These extend EvalProgressUpdate and add event-specific fields
 */

export interface EvalStartedPayload extends EvalProgressUpdate {
  eval_id: string
}

export interface EvalResultPayload extends EvalProgressUpdate {
  eval_id: string
  result?: unknown
  test_case_id?: string
}

export interface EvalCompletedPayload extends EvalProgressUpdate {
  eval_id: string
}

export interface EvalCancelledPayload extends EvalProgressUpdate {
  eval_id: string
  cancelled_by?: EvalCancellationSource
  reason?: string
  event_data?: {
    reason?: string
    node_id?: string
    test_name?: string
  }
  run_id?: number
}

export interface EvalErrorPayload {
  message?: string
  eval_id?: string
  error_code?: string
  [key: string]: unknown
}

/**
 * Backend payload type for test cases (snake_case)
 * Raw structure received from SSE events in dataset generation
 * Converted to EvalTestCase (camelCase) in store.ts when processing events
 * Used only in payload types (EvalTestCasePayload, TestCasePayload)
 */
export interface TestCaseData {
  test_id?: string
  name: string
  user_message: string
  node_uuid: string
  expected_variables: Record<string, unknown>
  node_variables?: Record<string, unknown>
  conversation_history: Array<{
    message: string
    role: string
    intent_chosen: string | null
    variables_extracted: Record<string, unknown>
    previous_node?: string | null
    new_node?: string | null
    tool_name?: string | null
    from_node_uuid?: string | null
    to_node_uuid?: string | null
    current_node_uuid?: string | null
  }>
  rigidity_category: string
}

export interface EvalTestCasePayload extends EvalProgressUpdate {
  eval_id: string
  test_case?: TestCaseData
}

export interface ResultPayload {
  test_id?: string
  test_name?: string
  success?: boolean
  metrics?: Array<{ name: string; score: number; threshold: number; reason: string }>
  metrics_data?: Array<{ name: string; score: number; threshold: number; reason: string }>
  actual_response?: string
  expected_response?: string
  event_data?: {
    test_id?: string
    test_name?: string
    success?: boolean
    metrics?: Array<{ name: string; score: number; threshold: number; reason: string }>
    metrics_data?: Array<{ name: string; score: number; threshold: number; reason: string }>
    actual_response?: string
    expected_response?: string
  }
}

export interface TestCasePayload {
  test_case?: TestCaseData
}

export interface SkippedNodePayload {
  node_uuid?: string
  reason?: string
  content_hash?: string
}
